import React from "react";

import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import EpgValidationReport from "./EpgValidationReport";
import MediaAPI from "../../../api/MediaAPI";
import { Button } from "../../ui/button";
import { ButtonGroup } from "../../ui/button-group";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

import type { JSX } from "react";

import type { GetMediaResponse } from "../../../api/MediaAPI.types";

interface Props {
  item: GetMediaResponse;
  onItemUpdate: (item: GetMediaResponse) => void;
}

export default function MediaEPG({ item, onItemUpdate }: Props): JSX.Element {
  const { t } = useTranslation("editor-content");

  const validation = useMutation({
    mutationFn: async (epgUrl: string) => MediaAPI.validateEpg(epgUrl),
  });

  const epgUrl = item?.external_stream?.epg_url ?? "";

  const handleChange = (value: string): void => {
    if (!validation.isIdle) validation.reset();

    onItemUpdate({
      ...item,
      external_stream: {
        type: "stream",
        url: null,
        url_type: null,
        player_code: null,
        ...item.external_stream,
        epg_url: value,
      },
    });
  };

  return (
    <div className="mt-7.5 flex max-w-3xl flex-col gap-3.75">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="epgUrl">EPG URL</Label>
        <ButtonGroup className="w-full">
          <Input
            id="epgUrl"
            onChange={(event): void => handleChange(event.target.value)}
            value={epgUrl}
          />
          <Button
            disabled={epgUrl.trim() === "" || validation.isLoading}
            size="lg"
            type="button"
            variant="outline"
            onClick={(): void => {
              validation.mutate(epgUrl.trim());
            }}
          >
            {t("epgValidation.validateButton")}
            {validation.isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : null}
          </Button>
        </ButtonGroup>
      </div>

      <EpgValidationReport
        hasFailed={validation.isError}
        result={validation.data}
      />
    </div>
  );
}
